import { LeaveAuditLog } from '../types/leavePolicy';
import { generateClient } from 'aws-amplify/api';

/**
 * Local cache of the server's audit trail — NOT the system of record.
 *
 * `logAction` stays synchronous, and deliberately so: it is called from the middle of a dozen
 * balance and approval operations that are not audit-aware and must not fail or slow down
 * because an audit write is in flight. It therefore writes the cache immediately and posts to
 * the server in the background. `getAuditLogs()` reads the cache, `refresh()` fills it.
 *
 * Before this the trail was localStorage-only, which made it actively misleading: every
 * reviewer saw only the actions taken in their own browser, so HR opening Audit History found
 * their own entries and no one else's — the one screen whose entire purpose is to show what
 * other people did.
 */
const AUDIT_LOGS_KEY = 'creyenteone:leave-audit-logs:v1';

/** Cache ceiling. The server keeps everything; this is only what one browser holds. */
const MAX_CACHED_LOGS = 1000;

let client: any = null;
const getClient = () => {
  if (!client) client = generateClient();
  return client;
};

const auditFields = `
  id leaveId action changedBy previousValue newValue reason employeeEmail employeeName timestamp
`;

const listLeaveAuditLogsQuery = /* GraphQL */ `
  query ListLeaveAuditLogs($limit: Int, $nextToken: String) {
    listLeaveAuditLogs(limit: $limit, nextToken: $nextToken) {
      items { ${auditFields} }
      nextToken
    }
  }
`;

const createLeaveAuditLogMutation = /* GraphQL */ `
  mutation CreateLeaveAuditLog($input: CreateLeaveAuditLogInput!) {
    createLeaveAuditLog(input: $input) { ${auditFields} }
  }
`;

/**
 * Map a row off the wire onto LeaveAuditLog.
 *
 * `leaveId` on the server is `entityId` on the client: the client field has always been the
 * generic id of whatever the entry is about (a leave, a comp-off, an adjustment), while the
 * GraphQL field was named for leaves. Renaming either side would have touched every call site
 * for no behavioural gain, so the mapping happens here, once.
 */
const normalize = (raw: any): LeaveAuditLog => ({
  id: String(raw?.id || ''),
  entityId: String(raw?.leaveId || ''),
  action: String(raw?.action || ''),
  changedBy: String(raw?.changedBy || ''),
  previousValue: raw?.previousValue || undefined,
  newValue: raw?.newValue || undefined,
  reason: raw?.reason || undefined,
  employeeEmail: raw?.employeeEmail || undefined,
  employeeName: raw?.employeeName || undefined,
  timestamp: String(raw?.timestamp || ''),
});

const readCache = (): LeaveAuditLog[] => {
  try {
    const raw = localStorage.getItem(AUDIT_LOGS_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const writeCache = (logs: LeaveAuditLog[]) => {
  localStorage.setItem(AUDIT_LOGS_KEY, JSON.stringify(logs.slice(0, MAX_CACHED_LOGS)));
};

export class LeaveAuditService {
  /** Cache read — synchronous by design, see the module note. */
  static getAuditLogs(): LeaveAuditLog[] {
    return readCache();
  }

  /**
   * Pull the trail from the server into the cache, newest first.
   *
   * Returns false when the server could not be reached, so a screen can say it is showing a
   * partial history instead of presenting a browser-local subset as the whole record.
   */
  static async refresh(): Promise<boolean> {
    try {
      const result = await getClient().graphql({
        query: listLeaveAuditLogsQuery,
        variables: { limit: 500 },
        authMode: 'userPool' as const,
      } as any);
      const items = ('data' in result ? result.data : null)?.listLeaveAuditLogs?.items;
      if (!Array.isArray(items)) return false;

      const sorted = items
        .map(normalize)
        .sort((a, b) => (b.timestamp || '').localeCompare(a.timestamp || ''));

      // Only announce a change when there is one — screens refresh on this event, and an
      // unconditional dispatch would have them trigger the refresh that re-fires it.
      const next = JSON.stringify(sorted.slice(0, MAX_CACHED_LOGS));
      if (next !== localStorage.getItem(AUDIT_LOGS_KEY)) {
        localStorage.setItem(AUDIT_LOGS_KEY, next);
        window.dispatchEvent(new Event('leave-audit-updated'));
      }
      return true;
    } catch (error) {
      console.warn('[LeaveAudit] refresh failed, showing cached trail:', error);
      return false;
    }
  }

  /**
   * Record one change.
   *
   * Synchronous return with a background server write. The alternative — making this async —
   * would mean awaiting an audit write inside every approval, adjustment and comp-off decision,
   * where a slow or failed audit call would then delay or appear to fail the operation itself.
   * An audit entry must never be the reason a legitimate action breaks.
   *
   * The trade-off is stated plainly: if the write fails the entry survives only in this
   * browser's cache and is lost on the next refresh. It is logged, not swallowed silently.
   */
  static logAction(
    entityId: string,
    action: string,
    changedBy: string,
    previousValue?: string,
    newValue?: string,
    reason?: string,
    employeeEmail?: string,
    employeeName?: string
  ): LeaveAuditLog {
    const entry: LeaveAuditLog = {
      id: `audit-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
      entityId,
      action,
      changedBy,
      previousValue,
      newValue,
      reason,
      employeeEmail,
      employeeName,
      timestamp: new Date().toISOString(),
    };

    writeCache([entry, ...readCache()]);

    getClient()
      .graphql({
        query: createLeaveAuditLogMutation,
        variables: {
          input: {
            leaveId: entityId || undefined,
            action,
            changedBy,
            previousValue: previousValue || undefined,
            newValue: newValue || undefined,
            reason: reason || undefined,
            employeeEmail: employeeEmail || undefined,
            employeeName: employeeName || undefined,
          },
        },
        authMode: 'userPool' as const,
      } as any)
      .then(() => window.dispatchEvent(new Event('leave-audit-updated')))
      .catch((error: unknown) =>
        console.warn('[LeaveAudit] entry saved locally but not on the server:', error)
      );

    return entry;
  }
}
